import React from 'react';
import { Input, Button, Dropdown, DatePicker, Switch } from 'antd';
import { FiSearch, FiFilter, FiDownload, FiRefreshCw } from 'react-icons/fi';
import { DownloadType, FilterStatus, MutationStatus } from 'src/enums/ELogs';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface FilterControlsProps {
	searchTerm: string;
	setSearchTerm: (value: string) => void;
	dateRange: [any, any] | null;
	setDateRange: (value: [any, any] | null) => void;
	statusFilter: string;
	setStatusFilter: (value: string) => void;
	durationFilter: string;
	setDurationFilter: (value: string) => void;
	hasActiveFilters: boolean;
	onRefresh: () => void;
	onDownload: (type: DownloadType) => void;
	isLoading: boolean;
	showActiveFilters?: boolean;
	downloadCallLogsStatus?: string;
	isNoviUser?: boolean;
	selectedAgentId?: string;
	provisionedNumbers?: any;
	hideEmptyConvos: boolean;
	setHideEmptyConvos: (checked: boolean) => void;
}

const FilterControls: React.FC<FilterControlsProps> = ({
	searchTerm,
	setSearchTerm,
	dateRange,
	setDateRange,
	statusFilter,
	setStatusFilter,
	durationFilter,
	setDurationFilter,
	hasActiveFilters,
	onRefresh,
	onDownload,
	isLoading,
	showActiveFilters = true,
	downloadCallLogsStatus = MutationStatus.IDLE,
	provisionedNumbers,
	hideEmptyConvos,
	setHideEmptyConvos,
}) => {
	const disabledDate = (current: dayjs.Dayjs) => {
		return current && current > dayjs().endOf('day');
	};

	const downloadMenu = (
		<div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[160px]">
			<div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
				Download Options
			</div>

			<button
				onClick={() => onDownload(DownloadType.CALLS)}
				disabled={
					downloadCallLogsStatus === MutationStatus.PENDING ||
					!provisionedNumbers?.provisioned_number
				}
				className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
					downloadCallLogsStatus === MutationStatus.PENDING ||
					!provisionedNumbers?.provisioned_number
						? 'text-gray-400 cursor-not-allowed'
						: 'text-gray-700 hover:bg-gray-100'
				}`}
			>
				<FiDownload className="w-4 h-4" />
				{downloadCallLogsStatus === MutationStatus.PENDING ? 'Downloading...' : 'Call Logs'}
			</button>

			<button
				onClick={() => onDownload(DownloadType.TEXTS)}
				className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-gray-700 hover:bg-gray-100"
			>
				<FiDownload className="w-4 h-4" />
				Text Logs
			</button>

			<button
				onClick={() => onDownload(DownloadType.VOICEMAILS)}
				className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-gray-700 hover:bg-gray-100"
			>
				<FiDownload className="w-4 h-4" />
				Voicemail Logs
			</button>
		</div>
	);

	const filterMenu = (
		<div
			className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80"
			onClick={(e) => e.stopPropagation()}
		>
			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
					<RangePicker
						className="w-full"
						value={dateRange}
						onChange={setDateRange}
						placeholder={['Start Date', 'End Date']}
						disabledDate={disabledDate}
						allowClear
						showTime={false}
					/>
				</div>

				<div className="flex gap-2 pt-2 border-t border-gray-200">
					<Button
						size="small"
						onClick={() => {
							setSearchTerm('');
							setDateRange(null);
							setStatusFilter(FilterStatus.ALL);
							setDurationFilter(FilterStatus.ALL);
						}}
					>
						Clear All
					</Button>
					<Button type="primary" size="small">
						Apply Filters
					</Button>
				</div>
			</div>
		</div>
	);

	return (
		<>
			<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
				<div className="relative flex-1 min-w-0">
					<div className="flex items-center">
						<div className="relative flex-1">
							<FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
							<Input
								placeholder="Search messages..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10 pr-10 border-gray-300 w-full h-8 sm:h-9 2xl:h-10"
								size="middle"
								autoComplete="off"
								allowClear={{
									clearIcon: (
										<span className="text-gray-400 hover:text-gray-600 cursor-pointer">×</span>
									),
								}}
							/>
						</div>
						<Dropdown overlay={filterMenu} trigger={['click']} placement="bottomRight">
							<Button
								className={`ml-2 border-gray-300 h-8 sm:h-9 2xl:h-10 flex items-center justify-center gap-1 sm:gap-2 transition-colors px-2 sm:px-3 text-xs sm:text-sm ${
									hasActiveFilters
										? 'border-blue-400 text-blue-600 bg-blue-50'
										: 'hover:border-blue-400 hover:text-blue-600'
								}`}
								title="Advanced Filters"
							>
								<FiFilter className="w-3 h-3 sm:w-4 sm:h-4" />
								<span className="hidden sm:inline">Filter</span>
							</Button>
						</Dropdown>
					</div>
				</div>

				<div className="flex items-center gap-3">
					<Button
						onClick={onRefresh}
						className="border-gray-300 h-8 sm:h-9 2xl:h-10 flex items-center justify-center gap-1 sm:gap-2 hover:border-blue-400 hover:text-blue-600 px-2 sm:px-3 text-xs sm:text-sm"
						title="Refresh Logs"
						loading={isLoading}
					>
						<FiRefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
						<span className="hidden sm:inline">Refresh</span>
					</Button>
					<Dropdown overlay={downloadMenu} trigger={['click']} placement="bottomRight">
						<Button
							className="border-gray-300 h-8 sm:h-9 2xl:h-10 flex items-center justify-center gap-1 sm:gap-2 hover:border-blue-400 hover:text-blue-600 px-2 sm:px-3 text-xs sm:text-sm"
							title="Download Logs"
						>
							<FiDownload className="w-3 h-3 sm:w-4 sm:h-4" />
							<span className="hidden sm:inline">Download</span>
						</Button>
					</Dropdown>
					<div className="flex items-center gap-2">
						<label htmlFor="hide-empty-toggle" className="text-sm text-gray-600 whitespace-nowrap">
							Hide empty calls
						</label>
						<Switch
							id="hide-empty-toggle"
							checked={hideEmptyConvos}
							onChange={setHideEmptyConvos}
						/>
					</div>
				</div>
			</div>
			{showActiveFilters && hasActiveFilters && (
				<div className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-100">
					<span className="text-sm text-gray-600">Active filters:</span>
					{searchTerm && (
						<span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
							Search: "{searchTerm}"
						</span>
					)}
					{dateRange && (
						<span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
							Date: {dateRange[0]?.format('MM/DD')} - {dateRange[1]?.format('MM/DD')}
						</span>
					)}
					{statusFilter !== FilterStatus.ALL && (
						<span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
							Status: {statusFilter}
						</span>
					)}
					{durationFilter !== FilterStatus.ALL && (
						<span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
							Duration: {durationFilter}
						</span>
					)}
					<button
						onClick={() => {
							setSearchTerm('');
							setDateRange(null);
							setStatusFilter(FilterStatus.ALL);
							setDurationFilter(FilterStatus.ALL);
						}}
						className="px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-red-200 rounded hover:bg-red-50"
					>
						Clear All
					</button>
				</div>
			)}
		</>
	);
};

export default FilterControls;
